import React from "react";
import { useNavigate } from "react-router-dom";
import Seo from "@/components/Seo";
import { PublicShell } from "@/public/PublicChrome";
import { Section, SectionHeading, Eyebrow, Lede, PrimaryCTA, SecondaryCTA, Reveal, Panel, Disclaimer, track } from "@/public/ui";
import { AttentionViz, AnalystViz, LockReadViz, CapabilityLadder, ProcessScoreViz, DailyViz, MarketPulse } from "@/public/visuals";
import { FaqAccordion } from "@/public/Faq";
import { SEO, faqJsonLd } from "@/public/seo";
import { ShieldCheck, Users, GitBranch, Scale, Eye } from "lucide-react";

const FAQ = [
  ["What is TRADECRAFT?", "TRADECRAFT is an AI-native market intelligence and trader-development platform. It scans supported crypto markets, surfaces what deserves attention, shows you the evidence, and develops your ability to make disciplined decisions — in a simulation-first, educational environment."],
  ["Does TRADECRAFT tell me what to buy or sell?", "No. TRADECRAFT never issues buy/sell signals or predicted returns. It surfaces situations that deserve attention and lays out supporting and conflicting evidence so you decide."],
  ["Does TRADECRAFT execute trades?", "No. There is no real-money execution and no brokerage connection. TRADECRAFT is a learning and analysis environment with simulation."],
  ["Is TRADECRAFT a trading bot?", "No. It is not automated trading or a signal service. The goal is to upgrade your judgment, not replace it."],
  ["How does TRADECRAFT use AI?", "A deterministic engine calculates objective market evidence (structure, trend, momentum, levels, risk/reward). AI then reasons over that evidence — it cannot silently invent or override the facts."],
  ["What is the difference between Analyst and Intelligence?", "Analyst produces structured analysis of a single market for you to evaluate. Intelligence scans the whole supported universe and surfaces the few situations that currently deserve attention."],
  ["Does paying unlock advanced capabilities automatically?", "No. A plan expands what's available, but advanced capabilities can still require demonstrated competency. For minors, guardian authorization is separate from earned competency."],
  ["Is TRADECRAFT financial advice?", "No. TRADECRAFT is educational. It does not provide financial advice, guarantee outcomes, or promise profit."],
];

export default function Home() {
  const navigate = useNavigate();
  const startFree = (from) => { track("hero_start_free_clicked", { from }); navigate("/signup"); };

  return (
    <PublicShell>
      <Seo {...SEO.home} noindex={false} jsonLd={{ "@context": "https://schema.org", "@graph": [...SEO.home.jsonLd["@graph"], faqJsonLd(FAQ)] }} />

      {/* HERO */}
      <Section className="pt-32 sm:pt-40 pb-20 sm:pb-28">
        <div className="absolute inset-0 -z-10 tc-grid-bg opacity-40" aria-hidden="true" />
        <div className="absolute inset-x-0 top-0 -z-10 h-[520px] bg-[radial-gradient(ellipse_at_top,rgba(6,182,212,0.14),transparent_65%)]" aria-hidden="true" />
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-10 items-center">
          <div>
            <Reveal><Eyebrow>AI-native market intelligence</Eyebrow></Reveal>
            <Reveal delay={0.05}>
              <h1 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-tc-txt">
                The market is noisy.<br /><span className="text-tc-coach">Your attention doesn't have to be.</span>
              </h1>
            </Reveal>
            <Reveal delay={0.1}>
              <Lede className="mt-6 max-w-xl">
                TRADECRAFT scans the market, surfaces what deserves attention, helps you understand the evidence, and develops your ability to make better decisions with AI.
              </Lede>
            </Reveal>
            <Reveal delay={0.12}>
              <p className="mt-4 max-w-xl text-sm text-tc-txt leading-relaxed" data-testid="hero-plain-line">
                TRADECRAFT combines AI-assisted market analysis with hands-on trading education and simulation. Explore market evidence, learn to challenge AI interpretations, and develop your own judgment—whether you're an experienced trader or just starting out. No brokerage services or real-money trading.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-8 flex flex-wrap gap-3">
                <PrimaryCTA data-testid="hero-start-free" onClick={() => startFree("hero")}>Start free — no card required</PrimaryCTA>
                <SecondaryCTA data-testid="hero-how-it-works" onClick={() => { track("hero_how_it_works_clicked"); navigate("/how-it-works"); }}>See How It Works</SecondaryCTA>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <Disclaimer className="mt-5">No credit card required · Educational simulation · No real-money execution</Disclaimer>
            </Reveal>
          </div>
          <Reveal delay={0.15} y={32}><AttentionViz /></Reveal>
        </div>
      </Section>

      {/* ATTENTION */}
      <Section className="py-20 sm:py-28">
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center">
          <div>
            <Reveal><Eyebrow>Attention</Eyebrow></Reveal>
            <Reveal delay={0.05}><SectionHeading className="mt-3">Twenty markets.<br />Three worth your attention.</SectionHeading></Reveal>
            <Reveal delay={0.1}><Lede className="mt-5 max-w-xl">Most traders don't need more charts. They need help knowing which charts matter right now. TRADECRAFT Intelligence screens supported markets, evaluates structure, and separates real movement from real structural change.</Lede></Reveal>
            <Reveal delay={0.15}>
              <div className="mt-6 flex flex-wrap gap-2">
                {["EARLY", "DEVELOPING", "NEAR TRIGGER", "TRIGGERED", "INVALIDATED"].map((s) => (
                  <span key={s} className="font-mono text-[10px] uppercase tracking-[0.14em] text-tc-sub border border-white/12 rounded-full px-3 py-1">{s}</span>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.2}><Disclaimer className="mt-5">Attention Priority ranks what to investigate first. It is not a probability of profit.</Disclaimer></Reveal>
          </div>
          <Reveal y={28}>
            <Panel className="p-6 relative overflow-hidden">
              <MarketPulse className="absolute bottom-0 inset-x-0 h-24 opacity-70" />
              <div className="relative">
                <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-tc-coach">Movement vs structure</div>
                <p className="mt-3 text-sm text-tc-sub leading-relaxed">A 9% candle is movement. A broken multi-week range is structure. TRADECRAFT tracks maturity — early base, developing setup, near trigger, triggered, invalidated — and flags conflicts instead of hype.</p>
                <div className="mt-6 grid grid-cols-3 gap-3 text-center">
                  {[["Screened", "20"], ["Attention", "13"], ["Changed", "7"]].map(([k, v]) => (
                    <div key={k} className="rounded-lg border border-white/8 bg-tc-bg/50 py-3">
                      <div className="font-heading text-2xl font-bold text-tc-txt">{v}</div>
                      <div className="font-mono text-[9px] uppercase tracking-wide text-tc-muted mt-1">{k}</div>
                    </div>
                  ))}
                </div>
              </div>
            </Panel>
          </Reveal>
        </div>
      </Section>

      {/* ANALYST */}
      <Section className="py-20 sm:py-28">
        <div className="max-w-2xl">
          <Reveal><Eyebrow tone="violet">Analyst</Eyebrow></Reveal>
          <Reveal delay={0.05}><SectionHeading className="mt-3">See the evidence.<br />Not just the answer.</SectionHeading></Reveal>
          <Reveal delay={0.1}><Lede className="mt-5">TRADECRAFT separates objective market evidence from AI interpretation. The deterministic engine calculates what's observable. The AI reasons over it — with a thesis, an alternative, conflicting evidence, and an explicit invalidation.</Lede></Reveal>
        </div>
        <Reveal delay={0.1} y={28} className="mt-10"><AnalystViz /></Reveal>
        <Reveal delay={0.15}><Disclaimer className="mt-5">"Actionable" describes evidence quality and setup readiness — never a guarantee of profit.</Disclaimer></Reveal>
      </Section>

      {/* HUMAN + AI */}
      <Section className="py-20 sm:py-28">
        <div className="max-w-2xl">
          <Reveal><Eyebrow tone="violet">Human + AI</Eyebrow></Reveal>
          <Reveal delay={0.05}><SectionHeading className="mt-3">Don't outsource your judgment.<br />Upgrade it.</SectionHeading></Reveal>
          <Reveal delay={0.1}><Lede className="mt-5">You analyze first. TRADECRAFT analyzes independently. Then you compare. The goal isn't to teach you to agree with AI — it's to learn when AI is useful, when evidence conflicts, and when your own thesis needs to change.</Lede></Reveal>
        </div>
        <Reveal delay={0.1} y={28} className="mt-10"><LockReadViz /></Reveal>
        <Reveal delay={0.15}>
          <p className="mt-10 text-center font-heading text-xl sm:text-2xl font-bold text-tc-txt">AI that helps you think.<br /><span className="text-tc-mastery">Not AI that tells you what to think.</span></p>
        </Reveal>
      </Section>

      {/* CAPABILITY */}
      <Section className="py-20 sm:py-28">
        <div className="max-w-2xl">
          <Reveal><Eyebrow>Capability</Eyebrow></Reveal>
          <Reveal delay={0.05}><SectionHeading className="mt-3">TRADECRAFT grows with you.</SectionHeading></Reveal>
          <Reveal delay={0.1}><Lede className="mt-5">From learning how to read a market to supervising professional AI analysis. Competency — not time spent watching lessons — determines progression.</Lede></Reveal>
        </div>
        <Reveal delay={0.1} y={28} className="mt-12"><CapabilityLadder /></Reveal>
        <Reveal delay={0.15}><Disclaimer className="mt-8">Buying a plan never manufactures competency. For minors, guardian authorization remains separate from demonstrated skill.</Disclaimer></Reveal>
      </Section>

      {/* LEARNING */}
      <Section className="py-20 sm:py-28">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <Reveal><Eyebrow>Learning</Eyebrow></Reveal>
            <Reveal delay={0.05}><SectionHeading className="mt-3">The market becomes the classroom.</SectionHeading></Reveal>
            <Reveal delay={0.1}><Lede className="mt-5">TRADECRAFT connects learning to actual market behavior — and won't make you relearn what you already know. It assesses competency and points you toward the gaps.</Lede></Reveal>
            <Reveal delay={0.15}><Disclaimer className="mt-5">Educational competency estimates — not professional certifications.</Disclaimer></Reveal>
          </div>
          <Reveal y={28}>
            <div className="grid grid-cols-2 gap-3">
              {[["Trader Path", "Adaptive route through the craft"], ["Today's Edge", "One sharp lesson, tied to now"], ["Historical Scenarios", "Replay real market structure"], ["Boss Markets", "Prove it under pressure"], ["Assess My Skills", "Find gaps, skip the rest"], ["Skill Mastery", "Structure · Risk · Confluence · Oversight"]].map(([k, v]) => (
                <Panel key={k} className="p-4">
                  <div className="font-heading font-semibold text-tc-txt text-sm">{k}</div>
                  <p className="mt-1 text-[12px] text-tc-sub leading-snug">{v}</p>
                </Panel>
              ))}
            </div>
          </Reveal>
        </div>
      </Section>

      {/* MID-PAGE FREE CTA */}
      <Section className="py-12">
        <div data-testid="mid-free-cta"><Panel className="p-6 sm:p-8 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="font-heading text-xl font-bold text-tc-txt">The Free plan is a real starting point.</div>
            <p className="mt-1 text-sm text-tc-sub">Start at Stage 1 with no experience, no card and no real money. Upgrade only if you want more.</p>
          </div>
          <PrimaryCTA data-testid="mid-start-free" onClick={() => startFree("mid")}>Start free — no card required</PrimaryCTA>
        </Panel></div>
      </Section>

      {/* PROCESS */}
      <Section className="py-20 sm:py-28">
        <div className="max-w-2xl">
          <Reveal><Eyebrow>Process</Eyebrow></Reveal>
          <Reveal delay={0.05}><SectionHeading className="mt-3">A good decision can lose.<br />A bad decision can win.</SectionHeading></Reveal>
          <Reveal delay={0.1}><Lede className="mt-5">That's why TRADECRAFT scores your process — not your luck. Plan adherence, risk discipline, rule violations, and reflection all count. And sometimes the best trade is no trade.</Lede></Reveal>
        </div>
        <Reveal delay={0.1} y={28} className="mt-10"><ProcessScoreViz /></Reveal>
      </Section>

      {/* DAILY */}
      <Section className="py-20 sm:py-28">
        <div className="max-w-2xl">
          <Reveal><Eyebrow>Daily</Eyebrow></Reveal>
          <Reveal delay={0.05}><SectionHeading className="mt-3">Open TRADECRAFT.<br />Know what matters.</SectionHeading></Reveal>
          <Reveal delay={0.1}><Lede className="mt-5">Every session starts with what deserves attention, what changed, your next move, what to learn, and where you're developing. TRADECRAFT remembers where you left off.</Lede></Reveal>
        </div>
        <Reveal delay={0.1} y={28} className="mt-10"><DailyViz /></Reveal>
      </Section>

      {/* TRUST */}
      <Section className="py-20 sm:py-28">
        <div className="max-w-2xl">
          <Reveal><Eyebrow>Trust</Eyebrow></Reveal>
          <Reveal delay={0.05}><SectionHeading className="mt-3">Built to show its work.</SectionHeading></Reveal>
          <Reveal delay={0.1}><Lede className="mt-5">A system you can question is more useful than one you're expected to trust blindly.</Lede></Reveal>
        </div>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            [GitBranch, "Deterministic where facts can be calculated", "Structure, levels and risk math are computed — not guessed."],
            [Eye, "AI where reasoning adds value", "The AI interprets evidence; it can't silently invent or override facts."],
            [Scale, "Supporting and conflicting evidence", "Both sides are shown, with an explicit invalidation for every thesis."],
            [ShieldCheck, "No look-ahead in historical training", "Replay and scenarios never leak the future into the past."],
            [Users, "Guardian architecture for minors", "Authorization is separate from competency; paying never bypasses it."],
            [ShieldCheck, "No execution, no guarantees", "No real-money trading, no brokerage, no promised outcomes."],
          ].map(([Icon, h, p], i) => (
            <Reveal key={h} delay={i * 0.05}>
              <Panel className="p-5 h-full">
                <Icon className="w-5 h-5 text-tc-coach" />
                <div className="mt-3 font-heading font-semibold text-tc-txt text-sm">{h}</div>
                <p className="mt-1.5 text-[13px] text-tc-sub leading-relaxed">{p}</p>
              </Panel>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* FAMILY */}
      <Section className="py-20 sm:py-28">
        <Reveal>
          <Panel glow="violet" className="p-8 sm:p-12 grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <Eyebrow tone="violet">Family</Eyebrow>
              <SectionHeading className="mt-3">Teach judgment before risking capital.</SectionHeading>
              <Lede className="mt-5">TRADECRAFT Family gives developing traders a simulation-first environment to learn market reasoning, risk, and AI oversight — with guardian controls that stay separate from earned competency.</Lede>
              <div className="mt-6"><SecondaryCTA data-testid="family-explore" onClick={() => { track("family_viewed", { from: "home" }); navigate("/family"); }}>Explore TRADECRAFT Family</SecondaryCTA></div>
            </div>
            <div className="space-y-3">
              {[["Competency is earned", "Skill is demonstrated, never purchased."], ["Guardian authorization is separate", "Advanced capabilities stay gated for minors."], ["Simulation-first", "No real-money execution, ever."]].map(([k, v]) => (
                <div key={k} className="rounded-xl border border-white/10 bg-tc-bg/50 px-4 py-3">
                  <div className="font-heading font-semibold text-tc-txt text-sm">{k}</div>
                  <p className="text-[13px] text-tc-sub mt-0.5">{v}</p>
                </div>
              ))}
            </div>
          </Panel>
        </Reveal>
      </Section>

      {/* PRICING teaser */}
      <PricingBlock navigate={navigate} />

      {/* FAQ */}
      <FaqAccordion items={FAQ} />

      {/* FINAL CTA */}
      <Section className="py-24 sm:py-32 text-center">
        <div className="absolute inset-x-0 bottom-0 -z-10 h-[420px] bg-[radial-gradient(ellipse_at_bottom,rgba(139,92,246,0.14),transparent_65%)]" aria-hidden="true" />
        <Reveal>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-tc-txt">Learn the market.<br /><span className="text-tc-coach">Master the craft.</span></h2>
        </Reveal>
        <Reveal delay={0.08}><Lede className="mt-6 max-w-xl mx-auto">Know what deserves your attention. Understand the evidence. Build the judgment to decide what comes next.</Lede></Reveal>
        <Reveal delay={0.14}>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <PrimaryCTA data-testid="final-start-free" onClick={() => startFree("final")}>Start Free</PrimaryCTA>
            <SecondaryCTA data-testid="final-login" onClick={() => navigate("/login")}>Log In</SecondaryCTA>
          </div>
        </Reveal>
        <Reveal delay={0.2}><Disclaimer className="mt-5">No credit card required · No real-money execution</Disclaimer></Reveal>
      </Section>
    </PublicShell>
  );
}

export function PricingBlock({ navigate, standalone = false }) {
  const tiers = [
    { name: "Free", price: "$0", sub: "Forever", features: ["Adaptive learning & competency assessment", "Trader Path", "Simulation & historical replay", "Limited Craft Coach / AI usage", "Skill tracking", "Limited view of surfaced situations"], highlight: false },
    { name: "Pro", price: "$39", per: "/mo", sub: "$29/mo billed annually · $348/yr", features: ["Everything in Free", "Analyst & Intelligence", "Watch", "Full Human + TRADECRAFT workflows", "Expanded contextual AI", "Higher AI usage allowance", "Professional analytical workspace"], highlight: true, note: "Advanced capabilities may still require demonstrated competency." },
    { name: "Family", price: "$59", per: "/mo", sub: "$44/mo billed annually · $528/yr", features: ["Everything in Pro", "Guardian dashboard", "Up to 4 linked learners", "Guardian authorization controls", "Shared family AI allowance", "Minor capability safeguards"], highlight: false },
  ];
  const start = (t) => { track("pricing_plan_clicked", { plan: t }); navigate("/signup"); };
  return (
    <Section className="py-20 sm:py-28" id="pricing">
      <div className="max-w-2xl">
        <Eyebrow>Pricing</Eyebrow>
        <SectionHeading className="mt-3">Start free. Grow when you're ready.</SectionHeading>
        <Lede className="mt-5">No credit card to create a free account. Plans expand what's available — they never bypass competency or guardian authorization.</Lede>
      </div>
      <div className="mt-10 grid md:grid-cols-3 gap-5" data-testid="pricing-grid">
        {tiers.map((t, i) => (
          <Reveal key={t.name} delay={i * 0.08}>
            <div className={`relative rounded-2xl border p-6 h-full flex flex-col ${t.highlight ? "border-tc-coach/50 bg-tc-card shadow-[0_0_36px_rgba(6,182,212,0.16)]" : "border-white/10 bg-tc-card/70"}`} data-testid={`plan-${t.name.toLowerCase()}`}>
              {t.highlight && <span className="absolute -top-3 left-6 rounded-full bg-tc-coach text-tc-bg font-mono text-[10px] uppercase tracking-wide px-3 py-1">Most popular</span>}
              <div className="font-mono text-[11px] uppercase tracking-[0.16em] text-tc-sub">{t.name}</div>
              <div className="mt-3 flex items-end gap-1">
                <span className="font-heading text-4xl font-extrabold text-tc-txt">{t.price}</span>
                {t.per && <span className="text-tc-muted mb-1">{t.per}</span>}
              </div>
              <div className="mt-1 font-mono text-[11px] text-tc-muted">{t.sub}</div>
              <ul className="mt-5 space-y-2 flex-1">
                {t.features.map((f) => (
                  <li key={f} className="flex gap-2 text-[13px] text-tc-sub leading-snug"><span className="text-tc-coach mt-0.5">›</span>{f}</li>
                ))}
              </ul>
              {t.note && <p className="mt-4 font-mono text-[10px] text-tc-muted leading-relaxed">{t.note}</p>}
              <button data-testid={`plan-cta-${t.name.toLowerCase()}`} onClick={() => start(t.name)}
                className={`mt-5 w-full rounded-xl py-3 text-sm font-semibold transition-all focus:outline-none focus:ring-2 focus:ring-tc-coach focus:ring-offset-2 focus:ring-offset-tc-bg ${t.highlight ? "bg-tc-coach text-tc-bg hover:opacity-90" : "border border-white/15 text-tc-txt hover:border-tc-coach/50 hover:text-tc-coach"}`}>
                Start Free
              </button>
            </div>
          </Reveal>
        ))}
      </div>
    </Section>
  );
}
